import { apiConfig, http } from "@api/base";
import { prepareDateQuery, prepareSumQuery } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializer";

const API_PATHS = {
  list: "/search-history/history/",
  item: "/search-history/history/:entryId",
  create: "/search-history/history/",
  update: "/search-history/history/:entryId",
  delete: "/search-history/history/:entryId",
  stats: "/search-history/stats/",
  search: "/search-history/search/",
  bulkDelete: "/search-history/bulk-delete/",
  test: "/search-history/test",
  // Add new endpoint for system-wide statistics
  systemStats: "/search-history/system-stats/",
  allUsersHistory: "/search-history/all-users/",
};

const DEFAULT_PARAMS = {
  days: 30,
  sort_by: "date",
  sort_order: "desc",
  page: 1,
  per_page: 50,
  success_only: false,
};

// Add new default params for system-wide queries
const SYSTEM_STATS_PARAMS = {
  days: 30,
  sort_by: "date",
  sort_order: "desc",
  page: 1,
  per_page: 100,
  success_only: false,
  include_all_users: true, // Key parameter for system-wide data
};

const handleResponse = (response) => {
  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }
  return response;
};

const getUserContext = () => {
  const currentUser = sessionManager.user;
  if (!currentUser) {
    throw new Error("User not authenticated - cannot save search history");
  }
  return currentUser.email || currentUser.username;
};

const cleanParams = (params) => {
  const cleaned = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== "" && value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const get = async (entryId) => {
  const path = generatePath(API_PATHS.item, { entryId });
  const response = await http.get(path);
  return handleResponse(response);
};

const create = async (searchData) => {
  const backendPayload = serializer.toJSON(searchData);

  if (!backendPayload.user_id) {
    backendPayload.user_id = getUserContext();
  }

  const response = await http.post(API_PATHS.create, backendPayload);
  return handleResponse(response);
};

const update = async (entryId, searchData) => {
  const path = generatePath(API_PATHS.update, { entryId });
  const backendPayload = serializer.toJSON(searchData);
  const response = await http.put(path, backendPayload);
  return handleResponse(response);
};

const deleteEntry = async (entryId) => {
  const path = generatePath(API_PATHS.delete, { entryId });
  return await http.delete(path);
};

// Updated getStats function for system-wide statistics
const getStats = async (params = {}) => {
  const queryParams = new URLSearchParams({
    days: params.days || 30,
    include_popular:
      params.include_popular !== undefined ? params.include_popular : true,
    include_recent:
      params.include_recent !== undefined ? params.include_recent : true,
    popular_limit: params.popular_limit || 10,
    recent_limit: params.recent_limit || 20,
    // Add system-wide flag for dashboard statistics
    include_all_users:
      params.include_all_users !== undefined ? params.include_all_users : false,
    ...params,
  });

  // Use system stats endpoint if requesting all users data
  const endpoint = params.include_all_users
    ? API_PATHS.systemStats
    : API_PATHS.stats;
  return await http.get(`${endpoint}?${queryParams}`);
};

// New function specifically for system-wide statistics (used by dashboard)
const getSystemWideStats = async (params = {}) => {
  const queryParams = new URLSearchParams({
    days: params.days || 30,
    include_popular: true,
    include_recent: true,
    include_analytics: true,
    include_user_metrics: true,
    include_performance_metrics: true,
    popular_limit: params.popular_limit || 20,
    recent_limit: params.recent_limit || 50,
    ...params,
  });

  return await http.get(`${API_PATHS.systemStats}?${queryParams}`);
};

const searchInHistory = async (searchTerm, params = {}) => {
  const queryParams = new URLSearchParams({
    q: searchTerm,
    days: params.days || 30,
    limit: params.limit || 50,
    // Add option to search across all users for system analysis
    include_all_users: params.include_all_users || false,
    ...params,
  });

  const endpoint = params.include_all_users
    ? API_PATHS.search
    : API_PATHS.search;
  return await http.get(`${endpoint}?${queryParams}`);
};

const bulkDelete = async (deleteParams) => {
  return await http.post(API_PATHS.bulkDelete, deleteParams);
};

const clearHistory = async () => {
  return await http.post(API_PATHS.bulkDelete, { delete_all: true });
};

const testConnection = async () => {
  return await http.get(API_PATHS.test);
};

class QueryBuilder {
  constructor() {
    this.searchTermQuery = [];
    this.dateRangeQuery = [];
    this.successQuery = [];
    this.scopeQuery = [];
    this.resultsCountQuery = [];
    this.size = "";
    this.page = "";
    this.sortBy = "";
    this.days = 30;
    this.includeAllUsers = false; // Add flag for system-wide queries
  }

  withSearchTerm(term) {
    if (!term || _isEmpty(term)) {
      throw TypeError("Search term argument missing");
    }
    this.searchTermQuery.push(`search_query:"${prepareSumQuery(term)}"`);
    return this;
  }

  withDateRange({ fromDate, toDate }) {
    if (fromDate || toDate) {
      this.dateRangeQuery.push(
        prepareDateQuery("created_at", null, fromDate, toDate)
      );
    }
    return this;
  }

  withDays(days) {
    if (days > 0) this.days = days;
    return this;
  }

  withScope(scope) {
    if (!scope || _isEmpty(scope)) {
      throw TypeError("Scope argument missing");
    }
    this.scopeQuery.push(`search_scope:"${prepareSumQuery(scope)}"`);
    return this;
  }

  withMinResults(minResults) {
    if (minResults >= 0) {
      this.resultsCountQuery.push(`results_count:>=${minResults}`);
    }
    return this;
  }

  successfulOnly() {
    this.successQuery.push("success:true");
    return this;
  }

  failedOnly() {
    this.successQuery.push("success:false");
    return this;
  }

  // Add method to include all users (for system-wide analysis)
  includeAllUsersData() {
    this.includeAllUsers = true;
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `&per_page=${size}`;
    return this;
  }

  withPage(page = 1) {
    if (page > 0) this.page = `&page=${page}`;
    return this;
  }

  sortByNewest() {
    this.sortBy = "&sort_by=date&sort_order=desc";
    return this;
  }

  sortByOldest() {
    this.sortBy = "&sort_by=date&sort_order=asc";
    return this;
  }

  sortByQuery() {
    this.sortBy = "&sort_by=query&sort_order=asc";
    return this;
  }

  sortByResultsCount() {
    this.sortBy = "&sort_by=results_count&sort_order=desc";
    return this;
  }

  sortByResponseTime() {
    this.sortBy = "&sort_by=response_time&sort_order=asc";
    return this;
  }

  qs() {
    const searchCriteria = this.searchTermQuery
      .concat(
        this.dateRangeQuery,
        this.successQuery,
        this.scopeQuery,
        this.resultsCountQuery
      )
      .join(" AND ");

    const queryString = searchCriteria
      ? `&search_term=${encodeURIComponent(searchCriteria)}`
      : "";

    const allUsersParam = this.includeAllUsers ? "&include_all_users=true" : "";

    return `days=${this.days}${queryString}${this.sortBy}${this.size}${this.page}${allUsersParam}`;
  }
}

const queryBuilder = () => new QueryBuilder();

// Updated list function with option for system-wide data
const list = async (params = {}) => {
  const finalParams = {
    ...DEFAULT_PARAMS,
    ...cleanParams(params),
  };

  const queryParams = new URLSearchParams(finalParams);

  // Use appropriate endpoint based on whether we need all users data
  const endpoint = finalParams.include_all_users
    ? API_PATHS.allUsersHistory
    : API_PATHS.list;
  const fullUrl = `${endpoint}?${queryParams}`;
  const response = await http.get(fullUrl);

  if (response.data.status === "success" && response.data.data) {
    const searchHistory = response.data.data;

    if (searchHistory.items && Array.isArray(searchHistory.items)) {
      response.data.hits = searchHistory.items.map((item) =>
        serializer.fromJSON(item)
      );
      response.data.total =
        searchHistory.pagination?.total || searchHistory.items.length;
      response.data.pagination = searchHistory.pagination;
    } else if (Array.isArray(searchHistory)) {
      response.data.hits = searchHistory.map((item) =>
        serializer.fromJSON(item)
      );
      response.data.total = searchHistory.length;
    } else {
      response.data.hits = [];
      response.data.total = 0;
    }
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

// New function specifically for dashboard - gets all users data
const listAllUsers = async (params = {}) => {
  const systemParams = {
    ...SYSTEM_STATS_PARAMS,
    ...cleanParams(params),
    include_all_users: true, // Always include all users for dashboard
  };

  return await list(systemParams);
};

const count = async (params = {}) => {
  const response = await list(params);
  return {
    ...response,
    data: response.data.total || 0,
  };
};

// Updated functions with system-wide options
const getUserRecentSearches = async (
  days = 7,
  limit = 20,
  includeAllUsers = false
) => {
  const params = {
    days,
    per_page: limit,
    sort_by: "date",
    sort_order: "desc",
    success_only: true,
    include_all_users: includeAllUsers,
  };
  return await list(params);
};

const getUserPopularSearches = async (days = 30, includeAllUsers = false) => {
  return await getStats({
    days,
    include_popular: true,
    include_all_users: includeAllUsers,
  });
};

const getSearchesByScope = async (
  searchScope,
  days = 30,
  includeAllUsers = false
) => {
  const params = {
    search_scope: searchScope,
    days,
    sort_by: "date",
    sort_order: "desc",
    include_all_users: includeAllUsers,
  };
  return await list(params);
};

const getSearchesByDateRange = async (
  startDate,
  endDate,
  includeAllUsers = false
) => {
  const params = {
    days: 365,
    sort_by: "date",
    sort_order: "desc",
    include_all_users: includeAllUsers,
  };
  return await list(params);
};

const getFailedSearches = async (days = 30, includeAllUsers = false) => {
  const params = {
    days,
    success_only: false,
    sort_by: "date",
    sort_order: "desc",
    include_all_users: includeAllUsers,
  };

  const response = await list(params);

  if (response.data.hits) {
    response.data.hits = response.data.hits.filter((hit) => !hit.success);
    response.data.total = response.data.hits.length;
  }

  return response;
};

// New system-wide analysis functions specifically for the dashboard
const getSystemFailedSearches = async (days = 30) => {
  return await getFailedSearches(days, true);
};

const getSystemSearchTrends = async (days = 30) => {
  return await listAllUsers({
    days,
    per_page: 1000, // Get more data for trend analysis
    sort_by: "date",
    sort_order: "desc",
  });
};

const getSystemPopularTerms = async (days = 30) => {
  return await getSystemWideStats({
    days,
    include_popular: true,
    popular_limit: 50,
  });
};

const saveSearch = async (searchQuery, metadata = {}) => {
  const searchData = {
    search_query: searchQuery.trim(),
    search_scope: metadata.search_scope || "all",
    results_count: metadata.total || metadata.results_count || 0,
    search_filters: metadata.filters || null,
    search_sort: metadata.sort || null,
    response_time_ms: metadata.response_time_ms || null,
    success: metadata.success !== undefined ? metadata.success : true,
    error_message: metadata.error_message || null,
    user_id: getUserContext(),
  };

  return await create(searchData);
};

const trackSearchPerformance = async (
  query,
  startTime,
  results,
  filters = {}
) => {
  const endTime = Date.now();
  const responseTime = endTime - startTime;

  const metadata = {
    total: results?.total || 0,
    filters:
      Object.keys(filters.filters || {}).length > 0 ? filters.filters : null,
    response_time_ms: responseTime,
    success: results?.total !== undefined && !results?.error,
    error_message: results?.error || null,
    sort: filters?.sort || null,
    search_scope: filters?.search_scope || "all",
  };

  return await saveSearch(query, metadata);
};

export const searchHistoryApi = {
  searchBaseURL: `${apiConfig.baseURL}${API_PATHS.list}`,
  query: queryBuilder,
  list,
  get,
  create,
  update,
  delete: deleteEntry,
  count,
  stats: getStats,
  searchInHistory,
  bulkDelete,
  clearHistory,
  testConnection,
  getUserRecentSearches,
  getUserPopularSearches,
  getSearchesByScope,
  getSearchesByDateRange,
  getFailedSearches,
  saveSearch,
  trackSearchPerformance,
  serializer,

  // New system-wide methods for dashboard
  getSystemWideStats,
  listAllUsers,
  getSystemFailedSearches,
  getSystemSearchTrends,
  getSystemPopularTerms,
};
